import { Chip, CircularProgress, Stack, Typography } from "@mui/material";
import { useErrorMessage } from "@poseidon/ui/app/toasts/useErrorMessage";
import DataGrid from "@poseidon/ui/components/DataGrid";
import { DataGridContainer } from "@poseidon/ui/components/DataGridContainer";
import { useSailorContext } from "../SailorContext";
import { filterOperators } from "../../DataGrid/ColumnHelpers";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SailingIcon from "@mui/icons-material/Sailing";
import DangerousIcon from "@mui/icons-material/Dangerous";
import { camelToSentenceCase } from "@poseidon/utils/text";
import { getDate } from "@poseidon/firebase/timestamp";
import { formatCurrency } from "@poseidon/utils";
import { WithDoc } from "@poseidon/firebase/components/WithDoc";
import { RagChip } from "@poseidon/ui/components/RagChip";
import { useSailorApplications } from "../hooks/useSailorApplications";

const columns = [
  {
    field: "applicationNumber",
    headerName: "Application Number",
    width: 100,
    filterOperators,
    hideable: false,
  },
  {
    field: "status",
    headerName: "Status",
    width: 100,
    filterOperators,
    //todo: copied from applications list - nice to have a reusable set of helpers/components for this
    renderCell: ({ value }) => {
      if (value === "active") {
        return (
          <Chip color="success" icon={<CheckCircleIcon />} label="Active" />
        );
      }
      if (value === "agreement") {
        return (
          <Chip color="primary" icon={<SailingIcon />} label="Agreement" />
        );
      }
      return (
        <Chip
          color="error"
          icon={<DangerousIcon />}
          label={camelToSentenceCase(value ?? "invalid")}
        />
      );
    },
  },
  {
    field: "createdDate",
    headerName: "Created Date",
    width: 100,
    filterOperators,
    valueGetter: ({ value }) => getDate(value)?.toLocaleDateString() ?? value,
  },

  {
    field: "principal",
    headerName: "Loan Amount",
    width: 100,
    valueGetter: ({ value }) => formatCurrency(value),
  },
  {
    field: "vessel",
    headerName: "Vessel",
    type: "string",
    width: 150,
    filterOperators,
    renderCell: ({ row }) => {
      if (!row.vessel) {
        return;
      }
      const boatId = row.vessel.id;
      return (
        <Stack>
          <WithDoc
            docRef={`boats/${boatId}`}
            Component={({ data: boat }) => (
              <>
                <Typography variant="p">
                  {boat.make ?? "Make Unknown"}: {boat.model ?? "Model Unknown"}
                </Typography>
                <Typography variant="caption">{boat.type}</Typography>
              </>
            )}
          />
        </Stack>
      );
    },
  },
  {
    field: "dealer",
    headerName: "Dealer",
    type: "string",
    width: 150,
    filterOperators,
    renderCell: ({ row }) => {
      if (!row.dealer) {
        return <RagChip label="No Dealer" />;
      }
      const dealerId = row.dealer.id;
      return (
        <Stack>
          <WithDoc
            docRef={`dealers/${dealerId}`}
            Component={({ data: dealer }) => dealer.companyName}
            fallbackValue={<RagChip label="No Dealer" />}
          />
        </Stack>
      );
    },
  },
];

export default function SailorApplications() {
  const sailor = useSailorContext();
  const { sailorApplications, loading, error } = useSailorApplications(
    sailor.id
  );
  const { setError } = useErrorMessage();

  if (error) {
    setError(error);
  }

  if (loading || !sailorApplications) {
    return (
      <CircularProgress sx={{ alignSelf: "center", justifySelf: "center" }} />
    );
  }

  return (
    <DataGridContainer>
      <DataGrid
        columns={columns}
        rows={sailorApplications}
        doubleClickSlug="applications"
        initialState={{
          sorting: {
            sortModel: [{ field: "createdAt", sort: "desc" }],
          },
        }}
      />
    </DataGridContainer>
  );
}
